import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus } from 'lucide-react';
import Header from './Header';

// Using relative URLs

const AddEmployee = () => {
  const { user, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'employee'
  });

  // Check if user is super admin
  const isSuperAdmin = currentWorkspace?.role === 'super_admin';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`/api/employees`, formData);
      toast.success('Employee added successfully!');
      navigate('/employees');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add employee');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/employees')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Employees
        </Button>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <UserPlus className="w-6 h-6 mr-2 text-navy-600" />
              Add New Employee
            </CardTitle>
            <CardDescription>
              Add a new employee to your organization. They will receive an email with login credentials.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john.doe@company.com"
                  className="h-12"
                />
                <p className="text-sm text-slate-500">
                  The employee will use this email to log in
                </p>
              </div>

              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="h-12"
                />
              </div>

              {/* Role */}
              <div className="space-y-2">
                <Label htmlFor="role">Role *</Label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full h-12 px-4 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500"
                  required
                >
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                  {isSuperAdmin && <option value="auditor">Auditor</option>}
                  {currentWorkspace?.role === 'owner' && <option value="owner">Owner</option>}
                </select>
                <div className="text-sm text-slate-500 space-y-1 mt-2">
                  <p><strong>Employee:</strong> Can view policies, complete trainings, and see device compliance</p>
                  <p><strong>Admin:</strong> Can manage employees, policies, and trainings</p>
                  {isSuperAdmin && <p><strong>Auditor:</strong> Can view and comment on assigned audits (read-only)</p>}
                  {currentWorkspace?.role === 'owner' && <p><strong>Owner:</strong> Full access including billing and organization settings</p>}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> A temporary password will be generated and sent to the employee's email address. 
                  They will be able to change it after their first login.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/employees')}
                  className="flex-1"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Employee'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddEmployee;
